import { useEffect } from 'react';
import { useError } from '../../../error-provider/ErrorProvider';
import { Box, Flex, Stack, Switch, Text, useToast } from '@chakra-ui/react';
import { useApolloClient, useMutation } from '@apollo/client';

import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import FormInput from '../../custom-components/Inputs/FormInput';
import ZaraButton from '../../custom-components/buttons/Button';

import { EmailNotification } from '../../../graphql/mutations/EmailNotification';
import CloseIcon from '../../custom-components/icons/CloseIcon';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';

export const emailNotificSchema = z.object({
    notificationEmails: z.array(
        z.object({
            value: z.string().max(255) // Max length for email, can be empty
        })
    ).min(1, "At least one email is required.").max(3, "Maximum of 3 emails allowed.")
});


const baseInputWrapperStyles = {
    border: '1px solid',
    borderColor: 'gray.200',
    borderRadius: 'md',
    p: '4',
};

const EmailNotifications = ({ data, agentId, myEmail }) => {
    const { showError } = useError();
    const toast = useToast();

    const isInitialEmailNotifications = !data && myEmail?.length;

    const apolloClient = useApolloClient();

    const active = data?.active ?? false;

    const { control, handleSubmit, reset, getValues } = useForm({
        defaultValues: {
            notificationEmails: [{ value: '' }], // Start with one email field
        },
        mode: 'onSubmit',
        resolver: zodResolver(emailNotificSchema),
    });

    const { fields, append, remove } = useFieldArray({
        control, // control from useForm
        name: "notificationEmails", // Name of the array field in the form
    });

    useEffect(() => {
        if (data) {
            const emailsToLoad = [];
            if (data.email1 && data.email1.trim()) {
                emailsToLoad.push({ value: data.email1 });
            }
            if (data.email2 && data.email2.trim()) {
                emailsToLoad.push({ value: data.email2 });
            }
            if (data.email3 && data.email3.trim()) {
                emailsToLoad.push({ value: data.email3 });
            }

            if (emailsToLoad.length === 0) {
                emailsToLoad.push({ value: '' }); // Ensure at least one field if no emails are set
            }
            reset({ notificationEmails: emailsToLoad });
        } else {
            reset({ notificationEmails: [{ value: '' }] }); // Default to one empty field if no data
        }
    }, [data, reset]);


    const [changeEmailNotification, { loading }] = useMutation(EmailNotification, {
        onError(error) {
            showError(undefined, error);
        },
    });

    async function onSubmit(formData) {
        const email_1_to_submit = formData.notificationEmails[0]?.value.trim() || '';
        const email_2_to_submit = formData.notificationEmails[1]?.value.trim() || '';
        const email_3_to_submit = formData.notificationEmails[2]?.value.trim() || '';

        const initialVars = { // Renamed to avoid conflict in correctVariables logic
            active: true,
            email1: myEmail ?? '',
            email2: '',
            email3: '',
        };

        const formVariables = { // Renamed to avoid conflict
            active: active,
            email1: email_1_to_submit,
            email2: email_2_to_submit,
            email3: email_3_to_submit,
        };

        const correctVariables = isInitialEmailNotifications ? initialVars : formVariables;

        try {
            await changeEmailNotification({
                variables: { ...correctVariables, agentId: agentId },
                onCompleted(mutationData) {
                    if (mutationData.emailNotification.__typename === 'SuccessType') {
                        toast({
                            status: 'success',
                            title: mutationData.emailNotification.message,
                            isClosable: true,
                        });

                        // Use the actual submitted values for cache update
                        const submittedValuesForCache = {
                            __typename: 'EmailNotificationType',
                            active: correctVariables.active,
                            email1: correctVariables.email1, // This will be from initialVars or formVariables
                            email2: correctVariables.email2,
                            email3: correctVariables.email3,
                        };

                        apolloClient.cache.modify({
                            id: `AgentType:${agentId}`,
                            fields: {
                                emailNotification() {
                                    return submittedValuesForCache;
                                },
                            },
                        });
                    }
                },
            });
        } catch (error) {}
    }

    async function changeTransfer(isActive) {
        const currentEmails = getValues("notificationEmails"); // Use getValues
        const email_1_val = currentEmails[0]?.value.trim() || '';
        const email_2_val = currentEmails[1]?.value.trim() || '';
        const email_3_val = currentEmails[2]?.value.trim() || '';

        const variablesToSubmit = {
            active: isActive,
            email1: email_1_val,
            email2: email_2_val,
            email3: email_3_val,
        };
        try {
            await changeEmailNotification({
                variables: { ...variablesToSubmit, agentId: agentId },
                onCompleted(mutationData) {
                    if (mutationData.emailNotification.__typename === 'SuccessType') {
                        apolloClient.cache.modify({
                            id: `AgentType:${agentId}`,
                            fields: {
                                emailNotification() {
                                    return {
                                        __typename: 'EmailNotificationType',
                                        active: variablesToSubmit.active,
                                        email1: variablesToSubmit.email1,
                                        email2: variablesToSubmit.email2,
                                        email3: variablesToSubmit.email3,
                                    };
                                },
                            },
                        });
                    }
                },
            });
        } catch (error) {}
    }

    const addMoreEmail = () => {
        // Logic to be updated using useFieldArray's append
        if (fields.length < 3) {
            append({ value: '' });
        }
    };

    // This useEffect is removed as initial submission logic needs re-evaluation
    // useEffect(() => {
    //     if (isInitialEmailNotifications) {
    //         // onSubmit(); // onSubmit now expects form data
    //     }
    // }, [data, myEmail, isInitialEmailNotifications, onSubmit]); // Added onSubmit to dep array if it were to be used

    return (
        <Box as='form' onSubmit={handleSubmit(onSubmit)}>
            <Flex
                fontSize={{ base: '24px', sm: '28px' }}
                lineHeight='28px'
                fontWeight={{ base: '400', sm: '700' }}
                textAlign='left'
                gap={{ base: '10px', sm: '10px' }}
                alignItems='center'
                mb='10px'
            >
                <Switch
                    isDisabled={loading}
                    isChecked={active}
                    onChange={() => changeTransfer(!active)} // This will use the updated changeTransfer
                    colorScheme='blackAlpha'
                />
                <Text fontSize='md'>Email Notifications</Text>
            </Flex>
            {active && (
                <Stack>
                    <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
                        {fields.map((field, index) => (
                            <FormInput
                                key={field.id} // Required by useFieldArray
                                isFocus={index === 0} // Optional: focus first input
                                control={control}
                                label={`Email Adress ${index + 1}`}
                                controllName={`notificationEmails.${index}.value`}
                                placeholder='Enter email adress'
                                {...baseInputWrapperStyles}
                                icon={index > 0 ? <CloseIcon /> : null}
                                iconHandler={index > 0 ? () => remove(index) : null}
                            />
                        ))}
                    </div>

                    <Stack direction='row'>
                        {fields.length < 3 && ( // Show button if less than 3 emails
                            <ZaraButton
                                onClick={addMoreEmail} // This will use the updated addMoreEmail
                                w='auto'
                                fontSize='sm'
                                leftIcon={<FontAwesomeIcon icon={faPlus} />}
                            >
                                Add additional email
                            </ZaraButton>
                        )}
                        <ZaraButton type='submit' isLoading={loading} w='auto' fontSize='sm'>
                            Save
                        </ZaraButton>
                    </Stack>
                </Stack>
            )}
        </Box>
    );
};

export default EmailNotifications;
